import * as React from 'react';
import { useDispatch } from 'react-redux';
import { Button, Modal } from '@rtkwlf/ui';
import { CredentialSecret, CredentialStatus } from '../../../../types';
import { useTrashablePromise } from '../../../../hooks';
import { apiRawFetch, apiRequest } from '../../../../APIClient';
import { SingleTicketViewLink } from '../SingleTicketViewLink';

export const useDeleteCredential = (getCredentialsLists: () => void) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [credentialToDelete, setCredentialToDelete] =
    React.useState<CredentialSecret | null>(null);

  const closeModal = React.useCallback(() => {
    setCredentialToDelete(null);
  }, []);

  const onConfirmDelete = React.useCallback((credential: CredentialSecret) => {
    setCredentialToDelete(credential);
  }, []);

  const { registerPromise } = useTrashablePromise();
  const dispatch = useDispatch();

  const onDelete = React.useCallback(async () => {
    if (!credentialToDelete) {
      return;
    }

    try {
      setIsLoading(true);
      const { ticket_id } = await registerPromise(
        apiRawFetch.deleteCredential(credentialToDelete),
      );

      const message =
        credentialToDelete.status === 'active' ? (
          <>
            Ticket
            <SingleTicketViewLink id={ticket_id} />
            for disconnecting <b>{credentialToDelete.name}</b> has been created
            and your Concierge Security Team will process it shortly.
          </>
        ) : (
          <>
            The pending operation for <b>{credentialToDelete.name}</b> has been
            cancelled.
            {ticket_id && (
              <>
                {` `}
                Ticket
                <SingleTicketViewLink id={ticket_id} />
                has been updated.
              </>
            )}
          </>
        );
      getCredentialsLists();
      dispatch(apiRequest.setGlobalStatus(message, 'success'));
    } catch (err) {
      dispatch(apiRequest.setGlobalStatus(err.message, err.color || 'danger'));
    } finally {
      setCredentialToDelete(null);
      setIsLoading(false);
    }
  }, [credentialToDelete, dispatch, registerPromise, getCredentialsLists]);

  const modal: React.ReactNode = React.useMemo(() => {
    if (!credentialToDelete) {
      return null;
    }

    let description: React.ReactNode;
    if (
      (credentialToDelete.status === CredentialStatus.EDIT ||
        credentialToDelete.status === CredentialStatus.CREATE) &&
      credentialToDelete.ticket_id
    ) {
      description = (
        <p>
          This action will cancel the pending connection request with your CST.
          Please refer to ticket{' '}
          <SingleTicketViewLink id={credentialToDelete.ticket_id} /> for more
          information.
        </p>
      );
    } else {
      description = (
        <p>
          This action will send a request to disconnect the account. Please
          contact your CST for more information.
        </p>
      );
    }

    return (
      <Modal
        visible
        title="Remove Account"
        onCancel={closeModal}
        onOk={onDelete}
        footer={
          <>
            <Button
              data-cy="cancel-remove"
              type="primary"
              ghost
              disabled={isLoading}
              onClick={closeModal}
            >
              Cancel
            </Button>
            <Button
              type="danger"
              data-cy="confirm-remove"
              onClick={onDelete}
              disabled={isLoading}
            >
              Remove Account
            </Button>
          </>
        }
      >
        <div style={{ maxWidth: 600 }}>
          <p>
            Are you sure you want to remove the account{' '}
            <b>{credentialToDelete.name}</b>?
          </p>
          {description}
        </div>
      </Modal>
    );
  }, [credentialToDelete, closeModal, onDelete, isLoading]);

  return {
    modal,
    onConfirmDelete,
  };
};
